import axios, { type AxiosResponse, type AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { endpoints } from "@/endpoints";
import { optimisticArrayUpdateMutation, tags } from "@/api/common";
import useApiStore from "@/stores/api";
import { useLocationSelector } from "@/composables/location";

import type {
  Status,
  CreateStatusRequest,
  StatusErrorResponse,
  UpdateStatusResponse,
  UpdateStatusRequest,
  UpdateStatusOrderRequest,
} from "@/api/statuses";

export const useCreateStatus = (
  onSuccess?: () => void,
  onError?: (data: AxiosError<StatusErrorResponse>) => void
) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const { selectedLocationGroupPk } = useLocationSelector();

  return useMutation(
    (payload: CreateStatusRequest) =>
      axios.post(apiStore.getEndpoint("platform", endpoints.statuses.create), {
        ...payload,
        locationGroupPk: selectedLocationGroupPk.value,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          tags.platform.statuses,
          { locationGroupPk: selectedLocationGroupPk.value },
        ]);
        onSuccess?.();
      },
      onError: onError,
    }
  );
};

export const useUpdateStatuses = (
  onSuccess?: (response: AxiosResponse<UpdateStatusResponse>) => void,
  onError?: (data: AxiosError<StatusErrorResponse>, status: Status) => void
) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const { selectedLocationGroupPk } = useLocationSelector();
  return useMutation<
    AxiosResponse<UpdateStatusResponse>,
    AxiosError<StatusErrorResponse>,
    Status,
    Status[]
  >(
    (status) => {
      const statusPayload: UpdateStatusRequest = {
        ...status,
        enabled: status.enabled ? 1 : 0,
      };
      const payload = {
        ...statusPayload,
        locationGroupPk: selectedLocationGroupPk.value,
      };
      return axios.put(
        apiStore.getEndpoint("platform", endpoints.statuses.update(payload.id)),
        payload
      );
    },
    {
      ...optimisticArrayUpdateMutation<
        AxiosResponse<UpdateStatusResponse>,
        AxiosError<StatusErrorResponse>,
        Status
      >(
        queryClient,
        () => [
          tags.platform.statuses,
          { locationGroupPk: selectedLocationGroupPk.value },
        ],
        "id"
      ),
      onSuccess,
      onError,
    }
  );
};

export const useUpdateStatusOrder = () => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const { selectedLocationGroupPk } = useLocationSelector();

  return useMutation(
    (statusRequest: UpdateStatusOrderRequest) => {
      const payload = {
        ...statusRequest,
        locationGroupPk: selectedLocationGroupPk.value,
      };
      return axios.put(
        apiStore.getEndpoint("platform", endpoints.statuses.update(payload.id)),
        payload
      );
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries([
          tags.platform.statuses,
          { locationGroupPk: selectedLocationGroupPk.value },
        ]);
      },
    }
  );
};

export const useDeleteStatus = () => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const { selectedLocationGroupPk } = useLocationSelector();

  return useMutation(
    (id: string) =>
      axios.delete(
        apiStore.getEndpoint("platform", endpoints.statuses.delete(id))
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          tags.platform.statuses,
          { locationGroupPk: selectedLocationGroupPk.value },
        ]);
      },
    }
  );
};
